import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import ReactDOM from 'react-dom'
import { Link, LinkProps } from 'react-router-dom'
import { tv } from 'tailwind-variants'
import { Cross, Drag, Search, Triangle } from 'components/icons'
import Spinner from 'components/spinner'
import Button from 'components/button'
import Tooltip from 'components/tooltip'
import { MenuLayer } from 'components/menu'

// The rows of a dropdown menu, in the manner of shadcn/ui: a padded list of rounded rows, each an
// icon, a label and, optionally, a keyboard shortcut or a › for a submenu, with muted section labels
// and hairline separators between groups. Put them in a <MenuList> inside a <Menu>.

// Rows match the sidebar's: 13px, medium, icons in the label's own colour. The weight is marked
// important because style.scss sets `font-weight: 400` on every a:link, which outranks one class,
// and a row that's a link would otherwise come out thin.
export const menuItem = tv({
  base: 'relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] font-medium! text-body select-none cursor-pointer outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover [&_svg]:shrink-0',
  variants: {
    variant: {
      default: '',
      destructive: 'text-red hover:bg-red/10 focus-visible:bg-red/10'
    },
    disabled: {
      true: 'pointer-events-none opacity-50'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
})

// Which of a list's submenus is open: one at a time, as in shadcn. Pointing at another row closes it, after
// a moment, so the pointer can cut across the rows below on its way into the submenu.
interface SubmenuGroup {
  open: string | null
  setOpen: React.Dispatch<React.SetStateAction<string | null>>
  closeSoon: () => void
  stay: () => void
}
const SubmenuContext = createContext<SubmenuGroup | null>(null)
const SUBMENU_CLOSE_DELAY = 150

/** The padded list the rows sit in, so their rounded hover doesn't touch the menu's edge. */
export const MenuList: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  const [open, setOpen] = useState<string | null>(null)
  const timer = useRef<number | undefined>(undefined)
  const stay = () => window.clearTimeout(timer.current)
  const closeSoon = () => {
    stay()
    timer.current = window.setTimeout(() => setOpen(null), SUBMENU_CLOSE_DELAY)
  }
  useEffect(() => () => window.clearTimeout(timer.current), [])

  return (
    <SubmenuContext.Provider value={{ open, setOpen, closeSoon, stay }}>
      <div className={`flex flex-col p-1${className ? ` ${className}` : ''}`}>{children}</div>
    </SubmenuContext.Provider>
  )
}

/** A muted heading over a group of rows ("Account", "Appearance"…). */
export const MenuLabel: React.FC<{
  children: React.ReactNode
  /** Heads something that's on, as All view actions marks one: in blue, with its dot. */
  active?: boolean
  'data-testid'?: string
}> = ({ children, active = false, 'data-testid': testId }) => (
  <div
    data-testid={testId}
    className={`flex items-center gap-1.5 px-2 pt-1.5 pb-1 text-xs font-medium select-none ${active ? 'text-blue' : 'text-muted'}`}
  >
    {children}
    {active && <span aria-hidden className="size-1.5 rounded-full bg-blue" />}
  </div>
)

/**
 * A footnote under the rows, as a shadcn card's footer: a hairline above, a quieter surface behind, running
 * to the menu's edges. Put it after the <MenuList>, not in it.
 */
export const MenuFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="border-t border-divider bg-surface-muted px-3 py-2.5 text-xs leading-4 text-muted whitespace-normal">
    {children}
  </p>
)

// The header's own controls: 24px, pulled into the band's padding so it stays 32px tall.
const headerControl = tv({
  base: 'h-6 -my-1 rounded-md text-muted hover:bg-surface-hover hover:text-body focus:shadow-none! focus:transform-none!',
  variants: {
    icon: { true: 'w-6 justify-center px-0 [&_svg]:size-3.5' },
    destructive: { true: 'px-1.5 text-xs font-medium hover:bg-red/10 hover:text-red' }
  }
})

/** A quiet action in a MenuHeader, set like its caption ("Clear all"). */
export const MenuHeaderAction: React.FC<{ onClick: () => void; disabled?: boolean; children: React.ReactNode }> = ({
  onClick,
  disabled,
  children
}) => (
  <Button
    internalType="transparent"
    className={headerControl({ destructive: true })}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </Button>
)

interface MenuHeaderProps {
  children: React.ReactNode
  /** Quiet actions at its right end, before any ×: MenuHeaderActions. */
  action?: React.ReactNode
  /** A × at its right end (in a draggable header, only once the menu has been moved). */
  onClose?: () => void
  /** Makes it the handle the menu is dragged by, with a grip in its gutter on hover (see FloatingMenu). */
  drag?: React.DOMAttributes<HTMLDivElement>
  /** The menu has been moved, so it stays open: the grip stays shown and the × comes in. */
  moved?: boolean
}

const header = tv({
  base: 'sticky top-0 z-10 flex items-center gap-2 border-b border-divider bg-surface-muted px-3 py-2 text-xs leading-4 font-medium text-muted select-none',
  variants: {
    draggable: { true: 'group/header cursor-grab active:cursor-grabbing touch-none' }
  }
})

// Shown on hover (always on a touch screen), and for good once the menu has been moved.
const grip = tv({
  base: 'absolute left-0 flex w-3 justify-center text-faint transition-opacity [&_svg]:size-3',
  variants: {
    moved: {
      true: 'opacity-100',
      false: 'opacity-0 group-hover/header:opacity-100 [@media(hover:none)]:opacity-100'
    }
  }
})

// The ×'s room opens up as it comes in, sliding the actions before it (Clear all) along rather than
// making them jump. Closed, a negative margin takes back the gap before it.
const closeSlot = tv({
  base: '-my-1 -mr-1.5 flex h-6 shrink-0 overflow-hidden transition-[width,margin,opacity] duration-200 ease-out motion-reduce:transition-none',
  variants: {
    shown: { true: 'ml-0 w-6 opacity-100', false: '-ml-2 w-0 opacity-0' }
  }
})

/**
 * A caption above the rows, as the footer is below them: a quieter surface, a hairline under it,
 * running to the menu's edges, and pinned while a long list scrolls beneath. Put it before the <MenuList>.
 * A floating menu's is its handle: a grip shows in the gutter on hover, and once the menu has been
 * moved the grip stays and a × comes in at the end to close it.
 */
export const MenuHeader: React.FC<MenuHeaderProps> = ({ children, action, onClose, drag, moved = false }) => {
  const closeShown = !drag || moved
  return (
    <div className={header({ draggable: !!drag })} {...drag}>
      {drag && (
        <span aria-hidden className={grip({ moved })}>
          <Drag />
        </span>
      )}
      {/* One line: a long caption ("Summarise <a long column name>") ends in an ellipsis, whole on hover. */}
      <TruncatedLabel className="block min-w-0 flex-1 truncate">{children}</TruncatedLabel>
      {action}
      {onClose && (
        <span
          data-testid="menu-header-close"
          className={closeSlot({ shown: closeShown })}
          inert={!closeShown}
          aria-hidden={!closeShown || undefined}
        >
          <Tooltip content="Close" className="w-max">
            <Button
              internalType="transparent"
              type="button"
              aria-label="Close"
              className={headerControl({ icon: true, className: 'my-0' })}
              onClick={onClose}
            >
              <Cross />
            </Button>
          </Tooltip>
        </span>
      )}
    </div>
  )
}

/** A hairline between groups of rows, running to the list's edges. */
export const MenuSeparator: React.FC = () => <div role="separator" className="-mx-1 my-1 h-px bg-divider" />

/**
 * A menu's search, as shadcn's command menu has it: a soft, rounded field with its icon inside, inset
 * in the menu, its icon and text lined up with the rows' below (4px in, then the rows' own 8px).
 * (data-slot keeps style.scss's rules for bare inputs off it.)
 */
export const MenuSearch = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, onChange, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)
    const [filled, setFilled] = useState(false)

    // Empties the field as typing would, so whoever listens hears the change, and keeps the cursor there.
    const clear = () => {
      const input = inputRef.current
      if (!input) return
      Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(input, '')
      input.dispatchEvent(new Event('input', { bubbles: true }))
      input.focus()
    }

    return (
      <div className="p-1 pb-0">
        <label className="flex h-8 cursor-text items-center gap-2 rounded-md bg-surface-sunken px-2 text-muted">
          <Search />
          <input
            ref={inputRef}
            data-slot="search"
            type="text"
            className={`h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-body shadow-none outline-none placeholder:text-muted ${className ?? ''}`}
            onChange={(event) => {
              setFilled(event.target.value !== '')
              onChange?.(event)
            }}
            {...props}
          />
          {filled && (
            <Button
              internalType="transparent"
              aria-label="Clear search"
              className="-mr-1 size-5 shrink-0 rounded-sm p-0 text-muted hover:bg-surface-hover hover:text-body focus:shadow-none! focus:transform-none! [&_svg]:size-3.5"
              onClick={clear}
              // Keeps focus in the field, so a list that closes when it loses focus stays open.
              onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
            >
              <Cross />
            </Button>
          )}
        </label>
      </div>
    )
  }
)
MenuSearch.displayName = 'MenuSearch'

/** The key that goes with a letter for a shortcut: ⌘ on a Mac ("⌘C"), Ctrl+ elsewhere ("Ctrl+C"). */
export const MOD_KEY = typeof navigator !== 'undefined' && /Mac/i.test(navigator.userAgent) ? '⌘' : 'Ctrl+'

/** A spinner in a row's icon's place, the same 16px, while what the row started is under way. */
export const MenuIconSpinner: React.FC = () => (
  <span className="flex size-4 shrink-0 items-center justify-center">
    <Spinner size="small" className="h-3.5 w-3.5 border-2" role="status" aria-label="Loading" />
  </span>
)

/** A row's label: when it's plain text cut short with an ellipsis (a long column name), the whole of it on hover. */
const TruncatedLabel: React.FC<{ className: string; children: React.ReactNode }> = ({ className, children }) =>
  typeof children === 'string' ? (
    <Tooltip content={children} showOnlyIfTruncated>
      <span className={className}>{children}</span>
    </Tooltip>
  ) : (
    <span className={className}>{children}</span>
  )

/** A disabled row that explains itself keeps the pointer, so its reason can show on hover. */
const EXPLAINED_DISABLED = 'opacity-50 cursor-default hover:bg-transparent'

interface MenuItemProps {
  children: React.ReactNode
  /** An icon before the label: the app's own icons, at their usual 16px. */
  icon?: React.ReactNode
  /** A keyboard shortcut shown at the row's end, e.g. "⇧⌘P". */
  shortcut?: string
  /** A › at the row's end, for a row that opens a submenu. */
  submenu?: boolean
  /** A second line under the label, in the menu's muted 12px: "Off while the table syncs". */
  description?: React.ReactNode
  /** Red, for actions that sign out, delete or leave. */
  variant?: 'default' | 'destructive'
  /** A small spinner at the row's end while what it started is under way. */
  loading?: boolean
  /** Something at the row's end, e.g. a switch for a setting the row turns on and off. */
  end?: React.ReactNode
  disabled?: boolean
  /** Why the row can't be chosen: it is disabled, with this as its tooltip and its accessible description. */
  disabledReason?: string
  /** `button` unless the row sits in a `role="menu"` list: `menuitem`, or `menuitemradio` for one of a set. */
  role?: 'button' | 'menuitem' | 'menuitemradio'
  /** For a `menuitemradio`: whether it's the current choice. */
  'aria-checked'?: boolean
  /** For a `submenu` row in a `role="menu"` list: it opens a menu, and whether that is open. */
  'aria-haspopup'?: 'menu'
  'aria-expanded'?: boolean
  /** Overrides the tab stop, e.g. -1 in a menu whose rows the arrow keys focus rather than Tab. */
  tabIndex?: number
  /**
   * Enter and Space (and → on a `submenu` row) click the row rather than call `onClick`, so anything
   * listening for its clicks, such as a product tour, sees the keys too.
   */
  keysClick?: boolean
  /** A page to go to: the row is then a link. */
  to?: LinkProps['to']
  /** What choosing the row does (after navigating, for a link). */
  onClick?: (event: React.SyntheticEvent<HTMLElement>) => void
  className?: string
  'data-cy'?: string
  'data-tour'?: string
  'data-testid'?: string
  id?: string
  /** E.g. to keep the grid's selection and focus while a cell's menu row is pressed. */
  onMouseDown?: React.MouseEventHandler<HTMLElement>
}

/**
 * A row of a menu: a link when it has `to`, otherwise an action. Either way it takes a tab stop
 * and answers Enter, and an action answers Space too, as a button does. In a
 * `role="menu"` list whose rows the arrow keys focus (the sidebar's menus), pass the row's `role`,
 * `tabIndex={-1}` and `keysClick`.
 */
export const MenuItem: React.FC<MenuItemProps> = ({
  children,
  icon,
  description,
  shortcut,
  submenu,
  variant,
  loading,
  end,
  disabled,
  disabledReason,
  role = 'button',
  tabIndex,
  keysClick,
  to,
  onClick,
  className,
  ...data
}) => {
  const reasonId = useId()
  const explained = disabledReason !== undefined
  const isDisabled = disabled || explained
  const classes = menuItem({
    variant,
    disabled: disabled && !explained,
    class: explained ? [EXPLAINED_DISABLED, className] : className
  })
  const submenus = useContext(SubmenuContext)
  // Pointing at a plain row closes the list's open submenu; a submenu's own row opens it instead.
  const onPointerEnter = submenu ? undefined : () => submenus?.closeSoon()
  const content = (
    <>
      {icon}
      {description ? (
        <span className="flex min-w-0 flex-1 flex-col">
          <TruncatedLabel className="truncate leading-5">{children}</TruncatedLabel>
          <span className="truncate text-xs leading-4 font-normal text-muted">{description}</span>
        </span>
      ) : (
        <TruncatedLabel className="min-w-0 flex-1 truncate">{children}</TruncatedLabel>
      )}
      {shortcut && <span className="ml-auto pl-4 text-xs tracking-widest text-muted">{shortcut}</span>}
      {submenu && <Triangle type="right" className="ml-auto text-muted" />}
      {loading && <Spinner size="small" className="ml-auto h-3.5 w-3.5 border-2" role="status" aria-label="Loading" />}
      {end && (
        // Kept to itself, so a click on a switch here isn't also a click on the row.
        <span className="ml-auto flex shrink-0 items-center pl-4" onClick={(event) => event.stopPropagation()}>
          {end}
        </span>
      )}
    </>
  )

  if (to !== undefined && !isDisabled) {
    return (
      <Link {...data} className={classes} to={to} onClick={onClick} onPointerEnter={onPointerEnter}>
        {content}
      </Link>
    )
  }

  const row = (
    <div
      {...data}
      role={role}
      tabIndex={tabIndex ?? (isDisabled ? -1 : 0)}
      aria-disabled={isDisabled || undefined}
      aria-describedby={explained ? reasonId : undefined}
      className={classes}
      onPointerEnter={onPointerEnter}
      onClick={(event) => {
        if (!isDisabled) onClick?.(event)
      }}
      onKeyDown={(event) => {
        if (keysClick) {
          if (event.key !== 'Enter' && event.key !== ' ' && !(submenu && event.key === 'ArrowRight')) return
          // Space would otherwise scroll the page. A disabled row ignores the click.
          event.preventDefault()
          event.currentTarget.click()
          return
        }
        if (isDisabled || (event.key !== 'Enter' && event.key !== ' ')) return
        // Space would otherwise scroll whatever the menu sits over.
        event.preventDefault()
        onClick?.(event)
      }}
    >
      {content}
    </div>
  )
  if (!explained) return row
  return (
    <>
      <Tooltip content={disabledReason}>{row}</Tooltip>
      <span id={reasonId} className="sr-only">
        {disabledReason}
      </span>
    </>
  )
}

// The gap between a menu and its submenu, and from the window's edges.
const SUBMENU_GAP = 4
const WINDOW_MARGIN = 10

// A submenu sized to its rows: between minWidth and width, and never wider than the window, its
// least width included, so on a narrow screen it still fits.
const fittedStyle = (
  minWidth: number,
  width: number,
  windowWidth: number,
  fittedWidth?: number
): React.CSSProperties => {
  const room = Math.max(0, windowWidth - WINDOW_MARGIN * 2)
  return {
    minWidth: Math.min(minWidth, room),
    maxWidth: Math.min(width, room),
    width: fittedWidth ?? 'max-content'
  }
}

interface MenuSubProps {
  children: React.ReactNode
  /** The row's label: "Switch view". */
  label: React.ReactNode
  icon?: React.ReactNode
  /** The submenu's width in px, or with `minWidth` the most it grows to. */
  width?: number
  /** Sizes the submenu to its rows, from this up to `width`, measured as it opens (as Menu's `minWidth`). */
  minWidth?: number
  'data-testid'?: string
}

/**
 * A row that opens a submenu beside the menu, in the manner of shadcn/ui's DropdownMenuSub: pointing at it
 * opens it, as do a click, Enter, Space and →; ← or pointing at another row closes it. It opens to the
 * right, or to the left when the right has no room, level with its row and kept on screen. Put rows in it
 * in a <MenuList>, as in the menu itself.
 */
export const MenuSub: React.FC<MenuSubProps> = ({ children, label, icon, width = 240, minWidth, ...data }) => {
  const group = useContext(SubmenuContext)
  const layer = useContext(MenuLayer)
  const id = useId()
  const [alone, setAlone] = useState(false)
  const open = group ? group.open === id : alone
  const row = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  // Opened from the keyboard, the submenu takes focus, so arrows and typing carry on inside it.
  const focusOnOpen = useRef(false)
  // Sized to its rows, the width it measured as it opened; it's measured afresh at each opening.
  const [fittedWidth, setFittedWidth] = useState<number>()
  // The window's width, for the screen cap on a submenu sized to its rows; kept as the window resizes.
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth)
  if (!open && fittedWidth !== undefined) setFittedWidth(undefined)

  const setOpen = (next: boolean) => {
    if (!group) return setAlone(next)
    group.stay()
    group.setOpen((current) => (next ? id : current === id ? null : current))
  }

  const focusFirstRow = (element: HTMLElement) =>
    element.querySelector<HTMLElement>('input, [tabindex="0"], a[href]')?.focus()

  const openFromKeyboard = () => {
    // Already open (pointed at), it doesn't render again, so take focus into it now.
    if (open && panel.current) return focusFirstRow(panel.current)
    focusOnOpen.current = true
    setOpen(true)
  }

  useLayoutEffect(() => {
    const anchor = row.current
    const element = panel.current
    if (!open || !anchor || !element) return
    if (minWidth && fittedWidth === undefined) {
      // The window may have resized while it was closed: measure under the current screen cap.
      if (windowWidth !== window.innerWidth) return setWindowWidth(window.innerWidth)
      setFittedWidth(Math.min(Math.ceil(element.getBoundingClientRect().width), width))
      return
    }

    const place = () => {
      const rowBox = anchor.getBoundingClientRect()
      // Beside the menu's edge rather than the row's, which sits inside the menu's padding.
      const menuBox = (anchor.closest('[data-menu]') ?? anchor).getBoundingClientRect()
      const right = menuBox.right + SUBMENU_GAP
      const panelWidth = element.offsetWidth
      const fitsRight = right + panelWidth <= window.innerWidth - WINDOW_MARGIN
      const left = fitsRight ? right : Math.max(WINDOW_MARGIN, menuBox.left - SUBMENU_GAP - panelWidth)
      // Its first row level with this one: the submenu's border and padding sit above it.
      const height = element.offsetHeight
      const top = Math.max(WINDOW_MARGIN, Math.min(rowBox.top - 5, window.innerHeight - WINDOW_MARGIN - height))
      element.style.left = `${left}px`
      element.style.top = `${top}px`
    }
    const onResize = () => {
      setWindowWidth(window.innerWidth)
      place()
    }
    onResize()
    if (focusOnOpen.current) {
      focusOnOpen.current = false
      focusFirstRow(element)
    }
    // Searching a submenu changes its height, which can move it to stay on screen.
    const observer = new ResizeObserver(place)
    observer.observe(element)
    window.addEventListener('resize', onResize)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', onResize)
    }
  }, [open, width, minWidth, fittedWidth, windowWidth])

  const submenu = open
    ? ReactDOM.createPortal(
        <div
          ref={panel}
          role="menu"
          tabIndex={-1}
          // A click in here is inside the menu, so the menu stays open (see Menu's click outside).
          data-nested-menu="true"
          data-menu=""
          className="fixed z-[1001] overflow-auto overscroll-contain rounded-lg border border-divider bg-surface-raised text-sm whitespace-nowrap shadow-[0_4px_12px_-2px_rgb(0_0_0/0.12),0_2px_4px_-2px_rgb(0_0_0/0.08)] animate-menu-in motion-reduce:animate-none"
          style={{
            ...(minWidth ? fittedStyle(minWidth, width, windowWidth, fittedWidth) : { width }),
            maxHeight: `calc(100vh - ${WINDOW_MARGIN * 2}px)`
          }}
          onPointerEnter={() => group?.stay()}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowLeft' || (event.target as HTMLElement).tagName === 'INPUT') return
            event.preventDefault()
            setOpen(false)
            row.current?.querySelector<HTMLElement>('[role="button"]')?.focus()
          }}
        >
          {/* Its rows answer to its own lists, not to the menu's: pointing at them keeps it open. */}
          <SubmenuContext.Provider value={null}>{children}</SubmenuContext.Provider>
        </div>,
        layer || document.getElementById('root') || document.body
      )
    : null

  return (
    <div
      ref={row}
      onPointerEnter={() => setOpen(true)}
      onKeyDown={(event) => {
        // Keys pressed in the submenu reach here too (React events cross portals); only the row's own count.
        if (event.key !== 'ArrowRight' || !row.current?.contains(event.target as Node)) return
        event.preventDefault()
        openFromKeyboard()
      }}
    >
      <MenuItem
        {...data}
        icon={icon}
        submenu
        aria-haspopup="menu"
        aria-expanded={open}
        className={open ? 'bg-surface-hover' : undefined}
        // Pointing at the row has usually opened it already, so a click only ever opens it, as in shadcn.
        onClick={(event) => (event.type === 'keydown' ? openFromKeyboard() : setOpen(true))}
      >
        {label}
      </MenuItem>
      {submenu}
    </div>
  )
}
